import { type RefObject, useEffect, useRef, useState } from 'react'

/**
 * Keeps keyboard focus inside a dialog and gives it back on close.
 *
 * AGM-124 names the first-use notice as one of three interaction patterns with an explicit
 * accessibility requirement, and the two halves people usually miss are here: Tab must wrap
 * at both ends (a Shift+Tab off the first control otherwise lands on the browser chrome and
 * the reader is stranded outside a dialog they cannot see), and focus must return to the
 * control that opened the dialog, or a keyboard user resumes at the top of the document.
 *
 * The focusable set is queried on every Tab rather than cached: the notice dialog's buttons
 * change as its request settles, and a cached list would trap focus on a control that no
 * longer exists.
 */
/*
 * `:not([tabindex="-1"])` on every term, not only the last.
 *
 * A `<button tabindex="-1">` is focusable by script and unreachable by Tab, which is exactly
 * what a listbox option is. Matching them here makes `last` an element the user can never
 * arrive at, so the wrap at the end of the list never fires: in the command palette, Tab from
 * the search field walked straight out of the dialog and into the page behind the scrim,
 * leaving the reader outside a modal they could not see and whose Escape handler was attached
 * to the node they had just left.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]'
]
  .map((term) => `${term}:not([tabindex="-1"])`)
  .join(', ')

export const useFocusTrap = (
  container: RefObject<HTMLElement | null>,
  { onEscape }: { onEscape?: () => void } = {}
): void => {
  /*
   * The handler is read through a ref so the trap sets up exactly once.
   *
   * Callers pass an inline arrow, which is a new function on every render of the page that
   * owns the dialog — and every mutation in flight re-renders that page. With `onEscape` in
   * the dependency list the effect would tear down and re-run constantly, and its cleanup
   * hands focus back to the element that opened the dialog: the reader would be thrown out
   * of the dialog and back onto the button, mid-Tab, several times per second.
   */
  const escapeRef = useRef(onEscape)
  useEffect(() => {
    escapeRef.current = onEscape
  }, [onEscape])

  /*
   * What opened it, read while rendering — before anything inside has mounted.
   *
   * A control inside with `autoFocus` (the search in "Add people") takes focus during the commit,
   * before any effect runs. Read in the effect, `document.activeElement` was already that search
   * box; on close, focus was "given back" to an element that no longer existed, and the reader
   * was left on the body instead of on the button they pressed (PRD-26 GRP-103).
   */
  const [opener] = useState(() => (document.activeElement instanceof HTMLElement ? document.activeElement : null))

  /*
   * The control inside that asked for focus, remembered across effect runs. React's development
   * build runs every effect twice — mount, clean up, mount — and the clean-up hands focus back to
   * the opener, so the second run would otherwise find focus outside and take it to the container.
   * Remembering it keeps the development build, and so the browser suite, telling the truth about
   * what a production build does.
   */
  const askedForFocus = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const node = container.current
    if (!node) return

    const focusables = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE))

    // The container itself is focused rather than the first button: a screen reader then
    // announces the dialog's heading before its controls, and no default action is one
    // stray Enter away. A control that asked for focus — a search box, opened to be typed
    // into — keeps it: the dialog is announced by name as focus enters it either way.
    const active = document.activeElement
    if (active instanceof HTMLElement && node.contains(active)) askedForFocus.current = active
    const wanted = askedForFocus.current
    if (wanted !== null && node.contains(wanted)) wanted.focus()
    else node.focus()

    // A control that disappears under the cursor — *Put back* on the last left-out file,
    // *Retry* on the last failed one — drops focus on the body, outside the trap, where
    // Escape no longer reaches the dialog. Focus is taken back by the container the moment
    // the tree changes and nothing inside it holds focus.
    const recover = () => {
      const active = document.activeElement
      if (active === null || active === document.body || !node.contains(active)) node.focus()
    }
    const observer = new MutationObserver(recover)
    observer.observe(node, { childList: true, subtree: true })

    const onKeyDown = (event: KeyboardEvent) => {
      const dismiss = escapeRef.current
      if (event.key === 'Escape' && dismiss) {
        event.preventDefault()
        dismiss()
        return
      }

      if (event.key !== 'Tab') return

      const items = focusables()
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        event.preventDefault()
        return
      }

      if (event.shiftKey && (document.activeElement === first || document.activeElement === node)) {
        event.preventDefault()
        last.focus()
        return
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    node.addEventListener('keydown', onKeyDown)
    return () => {
      observer.disconnect()
      node.removeEventListener('keydown', onKeyDown)
      opener?.focus()
    }
  }, [container, opener])
}
